/**
 * Utility functions for Team Planning features
 */

import type { WorkSlot } from '../types';
import type { Task } from '../types';

/**
 * Extract all scheduled task IDs from work slots
 */
export function getScheduledTaskIds(workSlots: WorkSlot[]): Set<string> {
  const scheduledIds = new Set<string>();

  for (const slot of workSlots) {
    if (slot.task_ids && Array.isArray(slot.task_ids)) {
      for (const taskId of slot.task_ids) {
        scheduledIds.add(taskId);
      }
    }
  }

  return scheduledIds;
}

/**
 * Filter tasks that are NOT scheduled in any work slot
 */
export function getUnscheduledTasks(tasks: Task[], workSlots: WorkSlot[]): Task[] {
  const scheduledIds = getScheduledTaskIds(workSlots);

  return tasks.filter(task =>
    !task.is_header &&
    task.status !== 'completed' &&
    !scheduledIds.has(task.id)
  );
}

/**
 * Filter tasks that are overdue (due_date < today and not completed)
 */
export function getOverdueTasks(tasks: Task[]): Task[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return tasks.filter(task => {
    if (task.is_header || task.status === 'completed' || !task.due_date) {
      return false;
    }

    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0);

    return dueDate < today;
  });
}

/**
 * Calculate days overdue for a task
 */
export function getDaysOverdue(dueDate: string | Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * User workload calculation result
 */
export interface UserWorkload {
  userId: string;
  totalHours: number;
  totalSlots: number;
  slotsByDay: Record<string, number>; // dateString -> hours
}

/**
 * Calculate workload for a specific user from work slots
 */
export function calculateUserWorkload(
  userId: string,
  workSlots: WorkSlot[],
  startDate?: Date,
  endDate?: Date
): UserWorkload {
  const userSlots = workSlots.filter(slot => slot.assigned_to === userId);

  let totalHours = 0;
  const slotsByDay: Record<string, number> = {};

  for (const slot of userSlots) {
    const start = new Date(slot.start_time);
    const end = new Date(slot.end_time);

    // Clamp allo range richiesto invece di scartare lo slot intero: uno slot
    // che sfora il bordo (es. sera che passa la mezzanotte, o a cavallo tra due
    // settimane) contribuisce con la sola porzione dentro il range, non con 0.
    const effStart = startDate && start < startDate ? startDate : start;
    const effEnd = endDate && end > endDate ? endDate : end;

    // Nessuna sovrapposizione col range → salta
    if (effEnd <= effStart) continue;

    const hours = (effEnd.getTime() - effStart.getTime()) / (1000 * 60 * 60);
    totalHours += hours;

    // Track by day (sull'inizio effettivo, così resta nel range)
    const dayKey = effStart.toISOString().split('T')[0];
    slotsByDay[dayKey] = (slotsByDay[dayKey] || 0) + hours;
  }

  return {
    userId,
    totalHours: Math.round(totalHours * 10) / 10, // Round to 1 decimal
    totalSlots: userSlots.length,
    slotsByDay
  };
}

/**
 * Calculate workload for multiple users
 */
export function calculateTeamWorkload(
  userIds: string[],
  workSlots: WorkSlot[],
  startDate?: Date,
  endDate?: Date
): Map<string, UserWorkload> {
  const workloadMap = new Map<string, UserWorkload>();

  for (const userId of userIds) {
    workloadMap.set(userId, calculateUserWorkload(userId, workSlots, startDate, endDate));
  }

  return workloadMap;
}

/**
 * Get workload status color based on hours
 */
export function getWorkloadStatusColor(hoursScheduled: number, targetHours: number = 40): string {
  const ratio = hoursScheduled / targetHours;

  if (ratio < 0.5) return '#22c55e'; // Green - underloaded
  if (ratio < 0.8) return '#eab308'; // Yellow - moderate
  if (ratio < 1.0) return '#f97316'; // Orange - heavy
  return '#ef4444'; // Red - overloaded
}

/**
 * Capacity color per la barra carico/capacità settimanale (soglia target 40h).
 * - verde  se < 80% (sotto 32h)
 * - ambra  se 80–100% (32–40h)
 * - rosso  se > 100% (oltre 40h, sovraccarico)
 */
export function getCapacityColor(hoursScheduled: number, targetHours: number = 40): string {
  const ratio = hoursScheduled / targetHours;
  if (ratio < 0.8) return '#0f7b34'; // verde
  if (ratio <= 1.0) return '#b25e09'; // ambra
  return '#b91c1c'; // rosso — sovraccarico
}

/**
 * True quando l'utente è in sovraccarico (oltre il target ore).
 */
export function isOverloaded(hoursScheduled: number, targetHours: number = 40): boolean {
  return hoursScheduled > targetHours;
}

/**
 * Mappa taskId -> ore schedulate (somma delle durate di tutti i work slot
 * il cui task_ids include il task). "Totale schedulato" su tutte le settimane,
 * così si capisce se un task è coperto rispetto alla stima, indipendentemente
 * dalla settimana visualizzata.
 */
export function getScheduledHoursByTask(workSlots: WorkSlot[]): Map<string, number> {
  const map = new Map<string, number>();

  for (const slot of workSlots) {
    if (!slot.task_ids || !Array.isArray(slot.task_ids) || slot.task_ids.length === 0) {
      continue;
    }
    const start = new Date(slot.start_time);
    const end = new Date(slot.end_time);
    const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    if (!isFinite(hours) || hours <= 0) continue;

    for (const taskId of slot.task_ids) {
      map.set(taskId, (map.get(taskId) || 0) + hours);
    }
  }

  return map;
}

/**
 * Group tasks by project
 */
export function groupTasksByProject(tasks: Task[]): Map<string, Task[]> {
  const grouped = new Map<string, Task[]>();

  for (const task of tasks) {
    const projectId = task.project_id;
    if (!grouped.has(projectId)) {
      grouped.set(projectId, []);
    }
    grouped.get(projectId)!.push(task);
  }

  return grouped;
}

/**
 * Sort tasks by priority and due date
 */
export function sortTasksByPriorityAndDueDate(tasks: Task[]): Task[] {
  const priorityOrder: Record<string, number> = {
    urgent: 0,
    high: 1,
    medium: 2,
    low: 3
  };

  return [...tasks].sort((a, b) => {
    // First by priority
    const priorityA = priorityOrder[a.priority || 'medium'] ?? 2;
    const priorityB = priorityOrder[b.priority || 'medium'] ?? 2;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Then by due date (earlier first, null last)
    if (a.due_date && b.due_date) {
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    }
    if (a.due_date) return -1;
    if (b.due_date) return 1;

    return 0;
  });
}

/**
 * Get priority color
 */
export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent': return '#ef4444';
    case 'high': return '#f97316';
    case 'medium': return '#eab308';
    case 'low': return '#22c55e';
    default: return '#6b7280';
  }
}

/**
 * Format hours for display
 */
export function formatHours(hours: number): string {
  if (hours < 1) {
    return `${Math.round(hours * 60)}min`;
  }
  return `${Math.round(hours * 10) / 10}h`;
}

// === Conflict detection (overlap) per la pianificazione team ======================

/**
 * Pure overlap check tra due intervalli temporali.
 * overlap = startA < endB && startB < endA (intervalli a contatto NON sono overlap).
 */
export function slotsOverlap(
  startA: Date,
  endA: Date,
  startB: Date,
  endB: Date
): boolean {
  return startA.getTime() < endB.getTime() && startB.getTime() < endA.getTime();
}

/**
 * Evento "leggero" usato dal conflict-check: qualsiasi impegno con un intervallo
 * e l'assegnatario noto. Compatibile sia con i work-slot (assigned_to) sia con
 * gli eventi Google mappati (extendedProps.assignedTo).
 */
export interface ConflictCandidate {
  id: string;
  start: Date;
  end: Date;
  assignedTo?: string;
}

/**
 * Helper puro: c'è almeno un impegno dello stesso assegnatario che si sovrappone
 * all'intervallo [start, end)?
 *
 * @param start        inizio del nuovo intervallo
 * @param end          fine del nuovo intervallo
 * @param assignedTo   utente assegnatario del nuovo intervallo
 * @param slots        work-slot esistenti (id, start_time, end_time, assigned_to)
 * @param googleEvents eventi Google dell'utente (extendedProps.assignedTo, start_time/end_time)
 * @param excludeId    id da escludere dal confronto (es. lo slot che stai spostando)
 */
export function hasConflict(
  start: Date,
  end: Date,
  assignedTo: string | undefined,
  slots: WorkSlot[],
  googleEvents: Array<{ id: string; start_time: string | Date; end_time: string | Date; extendedProps?: { assignedTo?: string } }>,
  excludeId?: string
): boolean {
  if (!assignedTo) return false;

  // Work-slot dello stesso assegnatario (escluso quello in movimento)
  const workSlotConflict = slots.some(slot => {
    if (slot.id === excludeId) return false;
    if (slot.assigned_to !== assignedTo) return false;
    return slotsOverlap(start, end, new Date(slot.start_time), new Date(slot.end_time));
  });
  if (workSlotConflict) return true;

  // Eventi Google dello stesso assegnatario
  return googleEvents.some(ev => {
    if (ev.id === excludeId) return false;
    if (ev.extendedProps?.assignedTo !== assignedTo) return false;
    return slotsOverlap(start, end, new Date(ev.start_time), new Date(ev.end_time));
  });
}
